import React from "react";
import Spin from "components/Spin/Spin";
import { Stack } from "@mui/material";
import dayjs from "dayjs";
import merchantJson from "../../../../locale/en/page/merchants.json";
import AmexCountryOptions from "utils/AmexOptions/AmexCountryOptions";

export const CustomComponentCommonProps = (
  label,
  value,
  type,
  mandatory = true,
  isEdit,
  loading = false,
) => {
  let props = {
    disabled: !isEdit || loading,
    label: `${label}`,
    value: value,
  };
  if (mandatory) props.mandatory = true;
  switch (type) {
    case "OutlinedText":
      props = {
        ...props,
        placeholder: "Please input",
      };
      break;
    case "Selection":
      props = {
        ...props,
        placeholder: "Please select",
        selectionValue: value,
      };
      break;
    case "CheckboxInput":
      props = {
        ...props,
        checked: value,
      };
      break;
  }
  return props;
};

export const spinning = () => (
  <Stack
    justifyContent={"center"}
    alignItems={"center"}
    sx={{
      minHeight: "100px",
      width: "100%",
      position: "relative",
    }}
  >
    <Spin />
  </Stack>
);

export const filteredFalsyData = (data) =>
  // Filter the object properties with null, undefined & "".
  Object.entries(data || {}).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key] = value;
    }
    return acc;
  }, {});

export const validationRules = {
  noLeadingWhitespace: (value) =>
    !/^\s/.test(value) || "First letter can not be whitespace.",
  noQuotesOrBackticks: (value) => !/["`]/.test(value) || "Wrong Format",
  noChineseCharacters: (value) =>
    !/[\u4e00-\u9fa5]/.test(value) ||
    "Only accept Alphabet characters, numbers, symbols and spaces.",

  onlyAlphabetAndSpaces: (value) =>
    /^[a-zA-Z\s]*$/.test(value) ||
    "Only accept Alphabet characters and spaces.",
  onlyAlphabetNumAndSpaces: (value) =>
    /^[a-zA-Z\d\s]*$/.test(value) ||
    "Only accept Alphabet characters, numbers and spaces.",
  onlyAlphabetNumChineseAndSpaces: (value) =>
    /^[\u4e00-\u9fa5a-zA-Z0-9 ]*$/.test(value) ||
    "Only accept Alphabet characters, Chinese characters, numbers and spaces.",
  onlyInteger: (value) => /^\d*$/.test(value) || "Only accept integers",
  onlyNumber: (value) => {
    if (value === "" || value === undefined || value === null) return true;
    return /^-?\d*\.?\d+$/.test(value) || "Only accept numbers";
  },
  onlyPositiveNumber: (value) => {
    if (value === "" || value === undefined || value === null) return true;
    return /^\d*\.?\d+$/.test(value) || "Only accept positive numbers";
  },
  onlyBlockLetter: (value) =>
    !value || /^[^a-z]*$/.test(value) || "Please enter in block letters",

  // DatePicker
  onlyBeforeToday: (value) =>
    !dayjs(value).isAfter(dayjs()) || "Please select a date before today.",

  onlyAfterToday: (value) =>
    !dayjs(value).isBefore(dayjs()) || "Please select a date after today.",

  // Phone Number
  validPhoneNumber: (value) =>
    /^[2-9][0-9]{7}$/.test(value) || "The phone number is in wrong format",

  // Email
  validEmail: (value) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) ||
    "Please enter a valid email address",

  // BR No.
  validBusinessRegNo: (value) =>
    /^[0-9]{8}(-?[0-9]){3}$/.test(value) || "BR Wrong Format.",

  // Bank Account
  validBankAccount: (value) => {
    if (!value) return true;
    // Define regex patterns
    const regex1 = /^\d{3}-\d{3}-\d{7,16}$/; // Standard format: 123-456-7890123456
    const regex2 = /^016-\d{7,16}$/; // Special case: 016-7890123456

    return (
      regex1.test(value) || regex2.test(value) || "Wrong bank account format"
    );
  },
  validBankBeneficiary: (value) => {
    if (!value) return true;
    return (
      /^[a-zA-Z0-9()><&\".,\'+\-\/ ]+$/.test(value) ||
      "Wrong Bank Beneficiary Format"
    );
  },

  // Full Width & Special Space
  noFullwidthCharacters: (value) =>
    !/[\uff01-\uffe6]/.test(value) || "Fullwidth characters is not allowed.",
  noSpecialSpaces: (value) =>
    !/[\u3000\u00A0]/.test(value) ||
    "Only halfwidth breaking space is allowed.",
};

export const rules = ({ required, maxLength, validationTags }) => {
  const rules = {};
  if (required) {
    rules.required = { value: true, message: "Mandatory" };
  }

  if (maxLength) {
    rules.maxLength = {
      value: maxLength,
      message: `Maximum length is ${maxLength} characters.`,
    };
  }

  if (validationTags?.length > 0) {
    const temp = validationTags.map((tag) => {
      return validationRules[tag];
    });
    rules.validate = { ...temp };
  }
  return rules;
};

export const filterDifferences = (
  oldObj,
  newObj,
  keysForComparison,
  keysToIgnore = [],
) => {
  const differences = {};
  const keys =
    Array.isArray(keysForComparison) && keysForComparison.length > 0
      ? keysForComparison
      : Object.keys(newObj);

  const isDeepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const sanitizeLogo = (logo) =>
    logo?.replace(/^data:image\/[a-z]+;base64,/, "") ?? "";

  const defaultHandler = (oldObj, newObj, key) => {
    const oldVal = oldObj[key];
    const newVal = newObj[key];
    return oldVal !== newVal ? { [key]: newVal } : null;
  };

  const createMdrHandler = (key) => (oldObj, newObj) => {
    const oldVal = parseFloat(oldObj[key] || 0).toFixed(4);
    const newVal = parseFloat(newObj[key] || 0).toFixed(4);
    const diff = {};
    if (newVal !== oldVal) diff[key] = (parseFloat(newVal) / 100).toFixed(4);
    return Object.keys(diff).length ? diff : null;
  };

  const handlers = {
    shareholderInfo: (oldObj, newObj, key) => {
      const oldVal = oldObj[key] ? JSON.parse(oldObj[key]) : null;
      const newVal = newObj[key];
      return !isDeepEqual(oldVal, newVal) ? { [key]: newVal } : null;
    },
    logo: (oldObj, newObj, key) => {
      const oldVal = oldObj[key];
      const newVal = sanitizeLogo(newObj[key]);
      return oldVal !== newVal ? { [key]: newVal } : null;
    },
    merchantLogo: (oldObj, newObj, key) => {
      const oldVal = oldObj[key];
      const newVal = sanitizeLogo(newObj[key]);
      return oldVal !== newVal ? { [key]: newVal } : null;
    },
    sales: (oldObj, newObj, key) => {
      const oldVal = oldObj[key]?.id;
      const newVal = newObj[key];
      return newVal !== oldVal ? { [key]: { id: newVal } } : null;
    },
    onboardSales: (oldObj, newObj, key) => {
      const oldVal = oldObj[key]?.id;
      const newVal = newObj[key];
      return newVal !== oldVal ? { [key]: { id: newVal } } : null;
    },
    referrer: (oldObj, newObj, key) => {
      const oldVal = oldObj[key]?.id;
      const newVal = newObj[key];
      return newVal !== oldVal ? { [key]: { id: newVal } } : null;
    },
    isKDollar: (oldObj, newObj, key) => {
      const newVal = newObj[key];
      const oldVal =
        oldObj[key] ||
        !!oldObj.otherInfo?.campaign?.find((c) => c === "KDOLLAR");
      return newVal !== oldVal
        ? { otherInfo: { campaign: newVal ? ["KDOLLAR"] : [] } }
        : null;
    },
    monthlyTransLimit: (oldObj, newObj, key) => {
      const oldVal = oldObj[key];
      const newVal = newObj[key];
      const diff = {};
      if (newVal !== oldVal) diff[key] = newVal;
      if (newVal === "0" || oldVal === "0" || newVal > oldVal)
        diff.exceedMonthlyTransLimit = "ACTIVE";
      return Object.keys(diff).length ? diff : null;
    },
    mdr: createMdrHandler("mdr"),
    foreignMdr: createMdrHandler("foreignMdr"),
    referrerCommission: createMdrHandler("referrerCommission"),
  };

  keys.forEach((key) => {
    if (keysToIgnore.includes(key)) return;
    const handler = handlers[key] || defaultHandler;
    const diffPart = handler(oldObj, newObj, key);
    if (diffPart) Object.assign(differences, diffPart);
  });

  return differences;
};

export const pickTheFieldsFrom = (obj, keys) => {
  // Ensure obj is a valid object
  if (typeof obj !== "object" || obj === null) {
    return {};
  }

  // Ensure keys is an array
  if (!Array.isArray(keys)) {
    return {};
  }

  return keys.reduce((acc, key) => {
    if (Object.hasOwn(obj, key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
};

export const INFO_KEYS = () => {
  const COMPANY_INFO_KEYS = Object.keys(
    merchantJson.merchantDetail.info.companyInfo.fields,
  );
  const MERCHANT_INFO_KEYS = Object.keys(
    merchantJson.merchantDetail.info.merchantInfo.fields,
  );
  const OTHERS_INFO_KEYS = Object.keys(
    merchantJson.merchantDetail.info.others.fields,
  );
  const ACCOUNT_INFO_KEYS = Object.keys(
    merchantJson.merchantDetail.info.accountInfo.fields,
  );
  return [
    ...COMPANY_INFO_KEYS,
    ...MERCHANT_INFO_KEYS,
    ...OTHERS_INFO_KEYS,
    ...ACCOUNT_INFO_KEYS,
  ];
};

export const CONFIG_KEYS = () => {
  const REPORT_KEYS = Object.keys(
    merchantJson.merchantDetail.config.report.fields,
  );
  const FINANCE_AND_SETTLEMENT_KEYS = Object.keys(
    merchantJson.merchantDetail.config.financeAndSettlement.fields,
  );
  const RECEIPT_KEYS = Object.keys(
    merchantJson.merchantDetail.config.receipt.fields,
  );
  const CAMPAIGN_KEYS = [
    ...Object.keys(merchantJson.merchantDetail.config.campaign.fields),
    "otherInfo",
  ];

  const OTHERS_KEYS = Object.keys(
    merchantJson.merchantDetail.config.others.fields,
  );

  return [
    ...REPORT_KEYS,
    ...FINANCE_AND_SETTLEMENT_KEYS,
    ...RECEIPT_KEYS,
    ...CAMPAIGN_KEYS,
    ...OTHERS_KEYS,
  ];
};

export const findCountryByCountryCode = (countryCode) => {
  const countryOptions = AmexCountryOptions("all");
  return countryOptions.find(
    (country) => country.value.split(":")[0] === countryCode,
  );
};
